import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from './firebaseConfig';

const NEUTRAL_SUCCESS_MESSAGE = 'If an account exists for that email, we’ve sent password-reset instructions. Please check your inbox and spam folder.';

const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
};

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function operationalErrorMessage(error) {
  if (error?.code === 'auth/too-many-requests') {
    return 'Too many password-reset requests. Please wait before trying again.';
  }
  if (error?.code === 'auth/network-request-failed') {
    return 'We couldn’t send the reset email. Check your connection and try again.';
  }
  return 'We couldn’t send the reset email. Please try again later.';
}

export default function ResetPasswordDialog({ open, email, onEmailChange, onClose }) {
  const [emailError, setEmailError] = useState('');
  const [requestError, setRequestError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const requestInFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    setEmailError('');
    setRequestError('');
    setSuccessMessage('');
  }, [open]);

  const handleClose = (_event, reason) => {
    if (requestInFlight.current) return;
    onClose(reason);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (requestInFlight.current) return;

    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setEmailError('Enter your email address.');
      return;
    }
    if (!isValidEmail(normalizedEmail)) {
      setEmailError('Enter a valid email address.');
      return;
    }

    onEmailChange(normalizedEmail);
    setEmailError('');
    setRequestError('');
    setSuccessMessage('');
    requestInFlight.current = true;
    setSubmitting(true);

    try {
      await sendPasswordResetEmail(auth, normalizedEmail);
      setSuccessMessage(NEUTRAL_SUCCESS_MESSAGE);
    } catch (error) {
      if (error?.code === 'auth/user-not-found') {
        setSuccessMessage(NEUTRAL_SUCCESS_MESSAGE);
      } else {
        setRequestError(operationalErrorMessage(error));
      }
    } finally {
      requestInFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="reset-password-dialog-title"
      aria-describedby="reset-password-dialog-description"
      fullWidth
      maxWidth="xs"
      PaperProps={{
        component: 'form',
        noValidate: true,
        onSubmit: handleSubmit,
        'aria-busy': submitting,
      }}
      data-testid="reset-password-dialog"
    >
      <DialogTitle id="reset-password-dialog-title">Reset your password</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <DialogContentText id="reset-password-dialog-description">
            Enter the email address you use for email-and-password sign-in. Firebase will email password-reset instructions if an account exists.
          </DialogContentText>
          <TextField
            id="reset-password-email"
            type="email"
            label="Email address"
            value={email}
            onChange={(event) => {
              onEmailChange(event.target.value);
              if (emailError) setEmailError('');
              if (requestError) setRequestError('');
              if (successMessage) setSuccessMessage('');
            }}
            autoComplete="email"
            autoFocus
            required
            fullWidth
            error={Boolean(emailError)}
            helperText={emailError}
            disabled={submitting}
            inputProps={{ 'data-testid': 'reset-password-email' }}
          />
          <Box role="status" aria-live="polite" sx={visuallyHidden}>
            {submitting ? 'Sending reset email.' : ''}
          </Box>
          {successMessage ? (
            <Alert severity="success" role="status" aria-live="polite" data-testid="reset-password-success">
              {successMessage}
            </Alert>
          ) : null}
          {requestError ? (
            <Alert severity="error" role="alert" data-testid="reset-password-error">
              {requestError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, flexWrap: 'wrap' }}>
        <Button type="button" onClick={handleClose} disabled={submitting}>
          Back to sign in
        </Button>
        <Button type="submit" variant="contained" disabled={submitting} data-testid="send-reset-email">
          {submitting ? 'Sending…' : 'Send reset email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
